import { Injectable } from '@angular/core';

import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map } from 'rxjs';

import { selectIsLinearBetslip } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { getSystemTypeKeyFromSlipId, isSystemSlipId } from '../../bet-generation/services/slip.utils';
import { BetslipTypesActions } from '../../types/actions';
import { betslipCurrentTypeSelector } from '../../types/base/selectors';
import BetslipErrorsActions from '../../validation/actions';
import BetslipSystemBetActions from '../actions';
import { systemBetStateSelector } from '../selectors';

@Injectable()
export default class StakeEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private userSettings: SyncUserSettingsService,
    ) {}

    setSystemBetStake$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.setStake),
            concatLatestFrom(() => this.store.select(betslipCurrentTypeSelector)),
            filter(([_, currentType]) => currentType === BetslipType.System),
            map(([action, _]) =>
                BetslipSystemBetActions.setSystemBetStake({
                    stake: action.stake,
                    silent: action.silent,
                    stakeStr: action.stakeStr,
                }),
            ),
        );
    });

    setDefaultStake$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.setSelectedType),
            concatLatestFrom(() => this.store.select(systemBetStateSelector)),
            filter(([action, state]) => action.betslipType === BetslipType.System && !state.stake),
            map(() =>
                BetslipSystemBetActions.setSystemBetStake({
                    stake: this.userSettings.defaultStake || null,
                    stakeStr: this.userSettings.defaultStake?.toString(),
                }),
            ),
        );
    });

    setSystemBetStakeFromStakeHint$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipErrorsActions.setStakeFromSlipStakeHint),
            concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
            filter(([stakeHint, isLinear]) => !isLinear && isSystemSlipId(stakeHint.slipStakeHint.slipId)),
            map(([{ slipStakeHint }]) => {
                return BetslipSystemBetActions.setSystemBetStake({ stake: slipStakeHint.newStakeHint, silent: true });
            }),
        );
    });

    setSystemTypeStakeFromStakeHint$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipErrorsActions.setStakeFromSlipStakeHint),
            concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
            filter(([stakeHint, isLinear]) => isLinear && isSystemSlipId(stakeHint.slipStakeHint.slipId)),
            map(([{ slipStakeHint }]) => {
                const systemTypeKey = getSystemTypeKeyFromSlipId(slipStakeHint.slipId)!;

                return BetslipSystemBetActions.setSystemBetTypeStakeValue({
                    systemTypeKey,
                    stake: slipStakeHint.newStakeHint,
                    silent: true,
                });
            }),
        );
    });
}
